import { IFirmware, IFirmwareStatistic } from '../../api/types';
import { canBeRolledOut } from '../../api/rules';

export const FIRMWARE_ROLLOUT_LIMITS = {
  maxDevicesBeforeQualityApproval: 1000,
  maxDevicesBeforeCurrent: 20000,
} as const;

export interface FirmwareWorkflowStepState {
  disabled: boolean;
  reason?: string;
}

export interface FirmwareWorkflowState {
  rolloutEligible: boolean;
  hasReportedDevice: boolean;
  maxDevicesPerRequest?: number;
  step1: FirmwareWorkflowStepState;
  step2: FirmwareWorkflowStepState;
  step3: FirmwareWorkflowStepState;
  step4: FirmwareWorkflowStepState;
  step5: FirmwareWorkflowStepState;
  step6: FirmwareWorkflowStepState;
}

export function getMaxDevicesPerRequest(
  firmware: IFirmware,
): number | undefined {
  if (!firmware.approvedByQualityAssurance) {
    return FIRMWARE_ROLLOUT_LIMITS.maxDevicesBeforeQualityApproval;
  }

  if (!firmware.isCurrent) {
    return FIRMWARE_ROLLOUT_LIMITS.maxDevicesBeforeCurrent;
  }

  return undefined;
}

export function getFirmwareWorkflowState(
  firmware: IFirmware,
  firmwareStatistic: IFirmwareStatistic,
  isCurrentProductionFirmware: boolean,
): FirmwareWorkflowState {
  const rolloutEligible = canBeRolledOut(firmware, firmwareStatistic);
  const hasReportedDevice = firmwareStatistic.reportedCount > 0;
  const betaFirmwareReasons = {
    qaApproval: 'Beta firmware cannot be accepted by QA.',
    updateDevices:
      'Beta firmware is limited to selected devices in Step 1.',
    setCurrent: 'Beta firmware cannot be set as current.',
    updateAllDevices: 'Beta firmware cannot be sent to all devices.',
    setProduction:
      'Beta firmware cannot be set as production firmware.',
  };

  return {
    rolloutEligible,
    hasReportedDevice,
    maxDevicesPerRequest: getMaxDevicesPerRequest(firmware),
    step1: {
      disabled: false,
    },
    step2: {
      disabled:
        !rolloutEligible ||
        (!hasReportedDevice && !firmware.approvedByQualityAssurance),
      reason: !rolloutEligible
        ? betaFirmwareReasons.qaApproval
        : !hasReportedDevice && !firmware.approvedByQualityAssurance
          ? 'At least one device must report this firmware first.'
          : undefined,
    },
    step3: {
      disabled: !rolloutEligible,
      reason: !rolloutEligible
        ? betaFirmwareReasons.updateDevices
        : undefined,
    },
    step4: {
      disabled:
        !rolloutEligible || !firmware.approvedByQualityAssurance,
      reason: !rolloutEligible
        ? betaFirmwareReasons.setCurrent
        : !firmware.approvedByQualityAssurance
          ? 'Firmware must be approved by QA first.'
          : undefined,
    },
    step5: {
      disabled:
        !rolloutEligible ||
        !firmware.approvedByQualityAssurance ||
        !firmware.isCurrent,
      reason: !rolloutEligible
        ? betaFirmwareReasons.updateAllDevices
        : !firmware.approvedByQualityAssurance
          ? 'Firmware must be approved by QA first.'
          : !firmware.isCurrent
            ? 'Firmware must be set as current first.'
            : undefined,
    },
    step6: {
      disabled:
        !rolloutEligible || !firmware.approvedByQualityAssurance,
      reason: !rolloutEligible
        ? betaFirmwareReasons.setProduction
        : !firmware.approvedByQualityAssurance
          ? 'Firmware must be approved by QA first.'
          : isCurrentProductionFirmware
            ? 'Already set as production firmware.'
            : undefined,
    },
  };
}
